/**
 * Lightweight connectivity probe that verifies actual server reachability.
 *
 * navigator.onLine is unreliable — it can report "online" behind captive
 * portals, dead WiFi, or when the server is down. This module does a real
 * HEAD request to /version.json (static, no auth, always available) to
 * confirm the server is reachable.
 *
 * Results are cached briefly (CACHE_TTL_MS) to avoid rapid-fire probes
 * when multiple events fire simultaneously (focus + online + visible).
 */

const PROBE_TIMEOUT_MS = 5000
const CACHE_TTL_MS = 5000

let cachedResult: boolean | null = null
let cachedAt = 0

/**
 * An abort signal that fires after `ms`, built from AbortController rather than
 * AbortSignal.timeout.
 *
 * Not stylistic. `AbortSignal.timeout` needs Chrome 103, and this file's whole
 * job is deciding whether the app is online — on an engine without it, the call
 * threw INSIDE probeConnectivity's try, the catch read that as "server
 * unreachable", and the app concluded it was permanently offline.
 *
 * What that costs, named precisely rather than as "the queue stops": the
 * RECONNECT path is the casualty. useNativeQueueAutoSync consults this probe
 * before syncing on a network/app event, so work queued while genuinely offline
 * is never retried once the connection returns. usePrefetchIssues and
 * chunkErrorHandler gate on it too. The immediate post-enqueue attempt in
 * api-client survives, because it asks Capacitor's getNetworkStatus instead —
 * so the failure is invisible to anyone testing while already online.
 *
 * That engine is not hypothetical: production telemetry found an Android 11
 * (API 30) device — four API levels above minSdk — running System WebView pinned
 * to Chrome/90, because System WebView is a separately Play-Store-updated APK.
 * minSdk constrains the OS, not the engine. See .browserslistrc, which records
 * the same finding.
 *
 * AbortController + setTimeout works everywhere the app runs. The timer is
 * cancelled by the caller so a fast response does not leave it pending.
 */
function timeoutSignal(ms: number): { signal: AbortSignal; cancel: () => void } {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)

  return { signal: controller.signal, cancel: () => clearTimeout(timer) }
}

/**
 * Probe whether the server is actually reachable by fetching a lightweight
 * static resource. Returns true if the server responds within the timeout,
 * false otherwise. Falls back to navigator.onLine if the probe itself errors
 * in an unexpected way.
 *
 * Results are cached for CACHE_TTL_MS to avoid hammering the server when
 * multiple sync triggers fire within the same window.
 */
export async function probeConnectivity(): Promise<boolean> {
  // Fast exit: browser says offline — no point probing
  if (!navigator.onLine) return false

  // Return cached result if fresh
  const now = Date.now()
  if (cachedResult !== null && now - cachedAt < CACHE_TTL_MS) {
    return cachedResult
  }

  const timeout = timeoutSignal(PROBE_TIMEOUT_MS)

  try {
    const response = await fetch('/version.json', {
      method: 'HEAD',
      cache: 'no-store',
      signal: timeout.signal,
    })
    cachedResult = response.ok
    cachedAt = Date.now()
    return cachedResult
  } catch {
    // Network error or timeout — server unreachable
    cachedResult = false
    cachedAt = Date.now()
    return false
  } finally {
    timeout.cancel()
  }
}

/**
 * Invalidate the cached probe result. Call when a network event fires
 * (online, networkStatusChange) so the next probe does a real check.
 */
export function invalidateProbeCache(): void {
  cachedResult = null
  cachedAt = 0
}
